/**
 * Inspections Dashboard - Phase 8 skeleton.
 *
 * Replaces Amanda's manual Excel sheets (`2025 Dashboard.xlsx`,
 * `QA Report_Unfinished Repairs Dashboard.xlsx`). MVP renders the
 * Repair Tracking widget. Five other widgets land in follow-up sessions:
 *   - Monthly Inspection Report (FTS performance, Actual vs Target)
 *   - Contractor Performance (10-year cross-tab)
 *   - Cancellation Summary (reason-code trending)
 *   - PJW Report (monthly export)
 *   - Year-End KPI (Inspection Matrix completion)
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  MapPin,
  TrendingUp,
  UserCheck,
  Users,
  Wrench,
  XCircle,
} from 'lucide-react'
import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { DataTable } from '@/admin/components/data-table'
import {
  useInspectionsDashboard,
  useInspectionsDashboardV2,
  type DashboardV2Row,
} from '@/admin/hooks/use-inspections'
import { useFormatters } from '@/shared/lib/formatting'

const AGING_LABEL: Record<string, string> = {
  '0_30_days': '0-30 days',
  '31_60_days': '31-60 days',
  '61_90_days': '61-90 days',
  over_90_days: '90+ days',
}

const AGING_ACCENT: Record<string, string> = {
  '0_30_days': 'bg-[var(--bg-light)] text-[var(--ink-2)]',
  '31_60_days': 'bg-[var(--status-info,#5a6356)]/15 text-[var(--ink)]',
  '61_90_days': 'bg-[var(--status-warn,#92400e)]/15 text-[var(--ink)]',
  over_90_days: 'bg-[var(--status-error,#991b1b)]/15 text-[var(--ink)]',
}

export function InspectionsDashboardPage() {
  const navigate = useNavigate()
  const { formatDate, formatTime } = useFormatters()
  const { data, isLoading } = useInspectionsDashboard()
  const [windowDays, setWindowDays] = useState(7)
  const { data: v2, isLoading: v2Loading } = useInspectionsDashboardV2(windowDays)

  const repair = data?.repair_tracking
  const totalAging = useMemo(() => {
    if (!repair) return 0
    return Object.values(repair.aging_buckets).reduce((a, b) => a + b, 0)
  }, [repair])

  return (
    <AppShell>
      <main className="page-container py-10">
        <header className="flex flex-col gap-3">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
                Inspections Dashboard
              </h1>
              <p className="mt-1 text-sm text-[var(--ink-3)]">
                Repair tracking across open punch-list items — aging,
                escalation flags, and the contractors with the most
                outstanding work.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/inspections')}
              >
                <ClipboardCheck size={14} className="mr-1.5" />
                List view
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/inspections/scheduling')}
              >
                <CalendarDays size={14} className="mr-1.5" />
                Calendar
              </Button>
            </div>
          </div>
        </header>

        {isLoading || !repair ? (
          <div className="mt-8 flex items-center justify-center py-12 text-[var(--ink-3)]">
            Loading dashboard…
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            {/* V2 spec tiles — Amanda's inspections-dashboard requirements */}
            <section>
              <header className="mb-3 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-base font-medium text-[var(--ink)]">
                    Inspections — your day
                  </h2>
                  <p className="text-xs text-[var(--ink-3)]">
                    Tiles from the inspections-dashboard spec. Showing the next{' '}
                    {windowDays} days; change with the buttons →
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {[7, 14, 30, 60].map((n) => (
                    <Button
                      key={n}
                      variant={windowDays === n ? 'default' : 'outline'}
                      size="xs"
                      onClick={() => setWindowDays(n)}
                    >
                      {n}d
                    </Button>
                  ))}
                </div>
              </header>
              {v2Loading || !v2 ? (
                <div className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-4 py-8 text-center text-xs text-[var(--ink-3)]">
                  Loading tiles…
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <SpecTileCard
                    tileId="dashboard-my-inspections"
                    title="My Inspections"
                    description={`Assigned to me, next ${windowDays} days`}
                    count={v2.counts.my_inspections}
                    icon={<UserCheck size={16} />}
                    rows={v2.my_inspections}
                    navigate={(uuid) => navigate(`/inspections/${uuid}`)}
                    emptyMessage="No inspections assigned to you in this window."
                    columns={[
                      {
                        key: 'date',
                        label: 'PJW Date',
                        render: (r) => (
                          <span className="whitespace-nowrap text-[var(--ink)]">
                            {formatDate(r.scheduled_for)}{' '}
                            <span className="text-[var(--ink-3)]">
                              {formatTime(r.scheduled_for)}
                            </span>
                          </span>
                        ),
                      },
                      {
                        key: 'status',
                        label: 'Appt',
                        render: (r) =>
                          r.appointment_confirmed ? (
                            <Badge variant="outline" className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]">
                              <CheckCircle2 size={10} className="mr-0.5" />
                              Confirmed
                            </Badge>
                          ) : (
                            <Badge
                              variant={isWithin7Days(r.scheduled_for) ? 'destructive' : 'outline'}
                            >
                              Unconfirmed
                            </Badge>
                          ),
                      },
                      {
                        key: 'contractor',
                        label: 'Contractor',
                        render: (r) => r.contractor_name || '—',
                      },
                      {
                        key: 'address',
                        label: 'Address',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {r.property_address_line_1}{' '}
                            <span className="text-[var(--ink-3)]">
                              {r.property_city}, {r.property_state}
                            </span>
                          </span>
                        ),
                      },
                      {
                        key: 'homeowner',
                        label: 'Homeowner',
                        render: (r) => r.homeowner_name || '—',
                      },
                      {
                        key: 'squares',
                        label: 'Squares',
                        render: (r) => r.square_count ?? '—',
                      },
                    ]}
                  />

                  <SpecTileCard
                    tileId="dashboard-pjw-not-closed"
                    title="PJW Not Closed"
                    description="Mine, past-due, no pass/fail yet"
                    count={v2.counts.pjw_not_closed}
                    icon={<AlertTriangle size={16} />}
                    rows={v2.pjw_not_closed}
                    navigate={(uuid) => navigate(`/inspections/${uuid}`)}
                    emptyMessage="No past-due PJWs awaiting your result."
                    columns={[
                      {
                        key: 'date',
                        label: 'PJW Date',
                        render: (r) => (
                          <span
                            className={
                              !r.appointment_confirmed && isWithin7Days(r.scheduled_for)
                                ? 'text-[var(--status-error,#991b1b)]'
                                : ''
                            }
                          >
                            {formatDate(r.scheduled_for)}
                          </span>
                        ),
                      },
                      {
                        key: 'contractor',
                        label: 'Contractor',
                        render: (r) => r.contractor_name || '—',
                      },
                      {
                        key: 'address',
                        label: 'Address',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {r.property_city}, {r.property_state}
                          </span>
                        ),
                      },
                      {
                        key: 'homeowner',
                        label: 'Homeowner',
                        render: (r) => r.homeowner_name || '—',
                      },
                      {
                        key: 'squares',
                        label: 'Squares',
                        render: (r) => r.square_count ?? '—',
                      },
                    ]}
                  />

                  <SpecTileCard
                    tileId="dashboard-inspections-scheduled"
                    title="Inspections Scheduled"
                    description={`All inspectors, next ${windowDays} days`}
                    count={v2.counts.inspections_scheduled}
                    icon={<CalendarDays size={16} />}
                    rows={v2.inspections_scheduled}
                    navigate={(uuid) => navigate(`/inspections/${uuid}`)}
                    emptyMessage="No upcoming inspections in this window."
                    columns={[
                      {
                        key: 'date',
                        label: 'PJW Date',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {formatDate(r.scheduled_for)}{' '}
                            <span className="text-[var(--ink-3)]">
                              {formatTime(r.scheduled_for)}
                            </span>
                          </span>
                        ),
                      },
                      {
                        key: 'status',
                        label: 'Appt',
                        render: (r) =>
                          r.appointment_confirmed ? (
                            <Badge variant="outline" className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]">
                              Confirmed
                            </Badge>
                          ) : (
                            <Badge
                              variant={isWithin7Days(r.scheduled_for) ? 'destructive' : 'outline'}
                            >
                              Unconfirmed
                            </Badge>
                          ),
                      },
                      {
                        key: 'fts',
                        label: 'Field Tech',
                        render: (r) => r.inspector_name || r.inspector_email || '—',
                      },
                      {
                        key: 'contractor',
                        label: 'Contractor',
                        render: (r) => r.contractor_name || '—',
                      },
                      {
                        key: 'address',
                        label: 'Address',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {r.property_city}, {r.property_state}
                          </span>
                        ),
                      },
                      {
                        key: 'homeowner',
                        label: 'Homeowner',
                        render: (r) => r.homeowner_name || '—',
                      },
                    ]}
                  />

                  <SpecTileCard
                    tileId="dashboard-awaiting-follow-up"
                    title="Awaiting Follow Up"
                    description="Inspections with a user flagged for action"
                    count={v2.counts.awaiting_follow_up}
                    icon={<Users size={16} />}
                    rows={v2.awaiting_follow_up}
                    navigate={(uuid) => navigate(`/inspections/${uuid}`)}
                    emptyMessage="No inspections currently flagged for follow-up."
                    columns={[
                      {
                        key: 'date',
                        label: 'PJW Date',
                        render: (r) => formatDate(r.scheduled_for),
                      },
                      {
                        key: 'fts',
                        label: 'Field Tech',
                        render: (r) => r.inspector_name || r.inspector_email || '—',
                      },
                      {
                        key: 'assignee',
                        label: 'Assigned to',
                        render: (r) => (
                          <Badge variant="secondary">
                            {r.assigned_to_name || r.assigned_to_email || '—'}
                          </Badge>
                        ),
                      },
                      {
                        key: 'contractor',
                        label: 'Contractor',
                        render: (r) => r.contractor_name || '—',
                      },
                      {
                        key: 'address',
                        label: 'Address',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {r.property_city}, {r.property_state}
                          </span>
                        ),
                      },
                    ]}
                  />

                  <SpecTileCard
                    tileId="dashboard-ytd-inspections"
                    title="YTD Inspections"
                    description="Completed this year — pass/fail breakdown below"
                    count={v2.counts.ytd_completed}
                    icon={<MapPin size={16} />}
                    rows={v2.ytd_completed}
                    navigate={(uuid) => navigate(`/inspections/${uuid}`)}
                    emptyMessage="No completed inspections this year yet."
                    columns={[
                      {
                        key: 'date',
                        label: 'Completed',
                        render: (r) => formatDate(r.completed_at),
                      },
                      {
                        key: 'fts',
                        label: 'Field Tech',
                        render: (r) => r.inspector_name || r.inspector_email || '—',
                      },
                      {
                        key: 'contractor',
                        label: 'Contractor',
                        render: (r) => r.contractor_name || '—',
                      },
                      {
                        key: 'address',
                        label: 'Address',
                        render: (r) => (
                          <span className="whitespace-nowrap">
                            {r.property_city}, {r.property_state}
                          </span>
                        ),
                      },
                      {
                        key: 'result',
                        label: 'Result',
                        render: (r) =>
                          r.result.startsWith('pass') ? (
                            <Badge variant="outline" className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]">
                              <CheckCircle2 size={10} className="mr-0.5" />
                              {r.result.replace(/_/g, ' ')}
                            </Badge>
                          ) : r.result.startsWith('fail') ? (
                            <Badge variant="destructive">
                              <XCircle size={10} className="mr-0.5" />
                              {r.result.replace(/_/g, ' ')}
                            </Badge>
                          ) : (
                            <Badge variant="outline">—</Badge>
                          ),
                      },
                    ]}
                  />
                </div>
              )}
            </section>

            {/* Summary cards row */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryCard
                icon={<Wrench size={16} />}
                label="Open punch-list items"
                value={repair.total_open_items}
                accent="warn"
              />
              <SummaryCard
                icon={<ClipboardCheck size={16} />}
                label="Resolved (all-time)"
                value={repair.total_resolved_items}
                accent="ok"
              />
              <SummaryCard
                icon={<AlertTriangle size={16} />}
                label="Aging > 60 days"
                value={
                  repair.aging_buckets['61_90_days'] +
                  repair.aging_buckets.over_90_days
                }
                accent="error"
              />
              <SummaryCard
                icon={<TrendingUp size={16} />}
                label="Inspections needing attention"
                value={repair.open_inspections.length}
                accent="info"
              />
            </div>

            {/* Aging buckets + Top contractors */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
                <h2 className="text-sm font-medium text-[var(--ink)]">
                  Punch-list aging
                </h2>
                <p className="text-xs text-[var(--ink-3)]">
                  Open items grouped by how long they've been open.
                  Items aging past 60 days are SOP escalation triggers.
                </p>
                <div className="flex flex-col gap-2">
                  {(Object.keys(AGING_LABEL) as Array<
                    keyof typeof AGING_LABEL
                  >).map((key) => {
                    const count = repair.aging_buckets[key]
                    const pct =
                      totalAging > 0 ? Math.round((count / totalAging) * 100) : 0
                    return (
                      <div key={key} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[var(--ink-2)]">
                            {AGING_LABEL[key]}
                          </span>
                          <span className="text-[var(--ink)]">
                            {count} <span className="text-[var(--ink-3)]">({pct}%)</span>
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-[var(--bg-light)]">
                          <div
                            className={`h-full ${AGING_ACCENT[key]}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>

              <section className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
                <h2 className="text-sm font-medium text-[var(--ink)]">
                  Top contractors by open items
                </h2>
                <p className="text-xs text-[var(--ink-3)]">
                  Five contractors with the most outstanding punch-list
                  items. Click through to the inspection list filtered
                  to their work (follow-up session).
                </p>
                {repair.top_contractors.length === 0 ? (
                  <p className="text-xs text-[var(--ink-3)]">
                    No open items - nothing to escalate.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {repair.top_contractors.map((c, i) => (
                      <div
                        key={c.contractor_uuid ?? `unknown-${i}`}
                        className="flex items-center justify-between rounded-md border border-[var(--rule)] bg-[var(--bg)] px-3 py-2"
                      >
                        <span className="text-xs text-[var(--ink-2)]">
                          {c.contractor_name}
                        </span>
                        <Badge variant="destructive">
                          {c.open_count} open
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Inspections needing attention */}
            <section className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
              <h2 className="text-sm font-medium text-[var(--ink)]">
                Inspections needing attention
              </h2>
              <p className="text-xs text-[var(--ink-3)]">
                Latest 20 inspections with at least one open punch-list
                item. Click any row to open the detail page and review
                the Repair Tracking tab.
              </p>
              {repair.open_inspections.length === 0 ? (
                <p className="text-xs text-[var(--ink-3)]">
                  Nothing needs attention right now.
                </p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {repair.open_inspections.map((inspection) => (
                    <button
                      key={inspection.uuid}
                      type="button"
                      onClick={() =>
                        navigate(`/inspections/${inspection.uuid}`)
                      }
                      className="flex items-center justify-between gap-3 rounded-md border border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left transition-colors hover:bg-[var(--bg-light)]"
                    >
                      <div className="flex flex-1 flex-col gap-0.5">
                        <span className="text-xs font-medium text-[var(--ink)]">
                          {inspection.code}
                          {' · '}
                          {inspection.sub_type_label}
                        </span>
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {inspection.contractor_name || '-'}
                          {inspection.property_city && (
                            <>
                              {' · '}
                              {inspection.property_city},{' '}
                              {inspection.property_state}
                            </>
                          )}
                        </span>
                      </div>
                      <Badge variant="outline">
                        {inspection.lifecycle_status.replace(/_/g, ' ')}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </AppShell>
  )
}

function SummaryCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode
  label: string
  value: number
  accent: 'warn' | 'ok' | 'error' | 'info'
}) {
  const accentClass: Record<typeof accent, string> = {
    warn: 'border-l-[var(--status-warn,#92400e)]',
    ok: 'border-l-[var(--status-ok,#1c5a48)]',
    error: 'border-l-[var(--status-error,#991b1b)]',
    info: 'border-l-[var(--status-info,#5a6356)]',
  }
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-lg border-l-[3px] border-y-[0.5px] border-r-[0.5px] border-y-[var(--rule)] border-r-[var(--rule)] ${accentClass[accent]} bg-[var(--bg-card)] p-4`}
    >
      <div className="flex items-center gap-2 text-[var(--ink-3)]">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.14em]">
          {label}
        </span>
      </div>
      <p className="font-display text-[28px] leading-none text-[var(--ink)]">
        {value}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// V2 spec tiles — Amanda's inspections-dashboard requirements
// ---------------------------------------------------------------------------

function isWithin7Days(value: string | null): boolean {
  // Use absolute delta so a past-due "scheduled_for" within the last 7
  // days still triggers the RED treatment on the PJW-Not-Closed tile —
  // the spec calls out unconfirmed within-7-days regardless of past/future.
  if (!value) return false
  const d = new Date(value).getTime()
  const now = Date.now()
  return Math.abs(d - now) < 7 * 24 * 60 * 60 * 1000
}

const SpecTileCard = ({
  title,
  description,
  count,
  icon,
  rows,
  columns,
  navigate,
  emptyMessage,
  tileId,
}: {
  title: string
  description: string
  count: number
  icon: React.ReactNode
  rows: DashboardV2Row[]
  columns: {
    key: string
    label: string
    render: (r: DashboardV2Row) => React.ReactNode
  }[]
  navigate: (uuid: string) => void
  emptyMessage: string
  tileId: string
}) => (
  <section className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)]">
    <header className="flex items-center justify-between border-b border-[var(--rule)] px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-[var(--ink-3)]">{icon}</span>
        <h3 className="text-sm font-medium text-[var(--ink)]">{title}</h3>
        <Badge variant="outline">{count}</Badge>
      </div>
      <span className="hidden text-[11px] text-[var(--ink-3)] sm:inline">
        {description}
      </span>
    </header>
    {rows.length === 0 ? (
      <div className="px-4 py-8 text-center text-xs text-[var(--ink-3)]">
        {emptyMessage}
      </div>
    ) : (
      <div className="max-h-[320px] overflow-y-auto">
        <DataTable<DashboardV2Row>
          tableId={tileId}
          columns={columns.map((c) => ({
            id: c.key,
            header: c.label,
            cell: c.render,
          }))}
          data={rows.slice(0, 20)}
          onRowClick={(row) => navigate(row.uuid)}
          rowKey="uuid"
        />
        {rows.length > 20 && (
          <div className="border-t border-[var(--rule)] px-4 py-2 text-center text-[11px] text-[var(--ink-3)]">
            Showing 20 of {rows.length} — open the list view for the rest.
          </div>
        )}
      </div>
    )}
  </section>
)
